import React from 'react';
import {
  Button,
  Card,
  Checkbox,
  Divider,
  Group,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
  Title,
  Select,
} from '@mantine/core';
import { IconCopy, IconTrash } from '@tabler/icons-react';
import type { CalendarRecord } from '../../../api/schedulingApis';
import { PermissionRestrictedTooltip } from '../../../permissions/scheduler';

interface CalendarDraft {
  name: string;
  type: string;
  timezone: string;
  isDefault: boolean;
  active: boolean;
}

interface CalendarOverviewTabProps {
  calendar: CalendarRecord | null;
  calendarDraft: CalendarDraft;
  setCalendarDraft: React.Dispatch<React.SetStateAction<CalendarDraft>>;
  copyCalendarName: string;
  setCopyCalendarName: React.Dispatch<React.SetStateAction<string>>;
  typeOptions: Array<{ value: string; label: string }>;
  timezoneOptions: Array<{ value: string; label: string }>;
  weeksCount: number;
  exceptionsCount: number;
  overridesCount: number;
  onUpdateCalendar: () => Promise<void>;
  onDeleteCalendar: () => Promise<void>;
  onCopyCalendar: () => Promise<void>;
  canEdit?: boolean;
  canDelete?: boolean;
  canCreate?: boolean;
}

export default function CalendarOverviewTab(props: CalendarOverviewTabProps) {
  const {
    calendar,
    calendarDraft,
    setCalendarDraft,
    copyCalendarName,
    setCopyCalendarName,
    typeOptions,
    timezoneOptions,
    weeksCount,
    exceptionsCount,
    overridesCount,
    onUpdateCalendar,
    onDeleteCalendar,
    onCopyCalendar,
    canEdit = true,
    canDelete = true,
    canCreate = true,
  } = props;

  return (
    <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md" style={{ height: '100%', minHeight: 0 }}>
      <Card withBorder radius="md" p="md" style={{ height: '100%', minHeight: 0, overflow: 'auto' }}>
        <Stack gap="sm">
          <Group justify="space-between" align="center">
            <Title order={4}>Calendar Information</Title>
            <PermissionRestrictedTooltip restricted={!canDelete}>
              <Button
                color="red"
                variant="subtle"
                size="xs"
                disabled={!calendar?.id || !canDelete}
                leftSection={<IconTrash size={12} />}
                onClick={() => {
                  void onDeleteCalendar();
                }}
              >
                Delete
              </Button>
            </PermissionRestrictedTooltip>
          </Group>
          <TextInput
            label="Name"
            value={calendarDraft.name}
            onChange={(e) => setCalendarDraft((s) => ({ ...s, name: e.currentTarget.value }))}
          />
          <Select
            label="Type"
            data={typeOptions}
            value={calendarDraft.type}
            onChange={(v) => setCalendarDraft((s) => ({ ...s, type: v || 'global' }))}
            disabled={!!calendar?.id}
          />
          <Select
            label="Timezone"
            data={timezoneOptions}
            value={calendarDraft.timezone}
            onChange={(v) => setCalendarDraft((s) => ({ ...s, timezone: v || 'UTC' }))}
            searchable
          />
          <Group>
            <Checkbox
              label="Default calendar"
              checked={calendarDraft.isDefault}
              onChange={(e) => setCalendarDraft((s) => ({ ...s, isDefault: e.currentTarget.checked }))}
            />
            <Checkbox
              label="Active"
              checked={calendarDraft.active}
              onChange={(e) => setCalendarDraft((s) => ({ ...s, active: e.currentTarget.checked }))}
            />
          </Group>
          <Group>
            <PermissionRestrictedTooltip restricted={!canEdit}>
              <Button
                variant="light"
                disabled={!calendar?.id || !canEdit}
                onClick={() => {
                  void onUpdateCalendar();
                }}
              >
                Update
              </Button>
            </PermissionRestrictedTooltip>
          </Group>
          <TextInput
            label="Copy calendar as"
            placeholder="New calendar name"
            value={copyCalendarName}
            onChange={(e) => setCopyCalendarName(e.currentTarget.value)}
            disabled={!calendar?.id || !canCreate}
          />
          <PermissionRestrictedTooltip restricted={!canCreate} fullWidth>
            <Button
              variant="default"
              leftSection={<IconCopy size={14} />}
              disabled={!calendar?.id || !canCreate}
              onClick={() => {
                void onCopyCalendar();
              }}
              fullWidth
            >
              Copy calendar
            </Button>
          </PermissionRestrictedTooltip>
        </Stack>
      </Card>

      <Card withBorder radius="md" p="md" style={{ height: '100%', minHeight: 0, overflow: 'auto' }}>
        <Stack gap="sm">
          {!calendar?.id ? (
            <div style={{ flex: 1, minHeight: 180, display: 'grid', placeItems: 'center' }}>
              <Text size="sm" c="dimmed" fw={600} style={{ letterSpacing: '0.02em' }}>
                Calendar not selected
              </Text>
            </div>
          ) : (
            <>
              <Group justify="space-between" align="flex-start" wrap="nowrap">
                <Title order={4}>{calendar.name || 'Selected calendar'}</Title>
                <Text size="10px" c="dimmed" ta="right">
                  {calendar.id || '-'}
                </Text>
              </Group>
              <Divider />
              <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="sm">
                <Card withBorder radius="md" p="sm">
                  <Text size="xs" c="dimmed">Weekly rules</Text>
                  <Text size="lg" fw={700}>{weeksCount}</Text>
                </Card>
                <Card withBorder radius="md" p="sm">
                  <Text size="xs" c="dimmed">Exceptions</Text>
                  <Text size="lg" fw={700}>{exceptionsCount}</Text>
                </Card>
                <Card withBorder radius="md" p="sm">
                  <Text size="xs" c="dimmed">Day overrides</Text>
                  <Text size="lg" fw={700}>{overridesCount}</Text>
                </Card>
              </SimpleGrid>
            </>
          )}
        </Stack>
      </Card>
    </SimpleGrid>
  );
}
